// Mantine
import { TextInput, Button } from '@mantine/core'
import { useForm } from '@mantine/form'

// Zod
import { z } from 'zod'
import { zod4Resolver } from 'mantine-form-zod-resolver'

// Components
import CountrySelect from '@/components/ui/CountrySelect'

// React
import { useEffect, useState } from 'react'

import { usePlacesWidget } from 'react-google-autocomplete'

// Validation schema

const consigneeSubmitSchema = z
    .object({
        name: z.string(),
        email: z
            .string()
            .refine(
                (val) => {
                    if (!val || val === '') return true
                    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
                },
                { message: 'Email invalide' }
            )
            .min(1, "L'email est requis"),
        phone: z.string().min(1, 'Le numéro de téléphone est requis'),
        company: z.string(),
        address1: z.string().min(1, "L'adresse est requise"),
        address2: z.string(),
        city: z.string().min(1, 'La ville est requise'),
        zip: z.string().min(1, 'Le code postal est requis'),
        country_code: z.string().min(1, 'Le pays est requis'),
    })
    .refine((data) => data.name.trim() !== '' || data.company.trim() !== '', {
        message: 'Le nom ou l\'entreprise doit être rempli',
        path: ['name'],
    })

export default function AddressForm({ initialValues = {}, onSubmit, loading = false }) {
    const form = useForm({
        validate: zod4Resolver(consigneeSubmitSchema),
        initialValues: {
            name: initialValues.name ?? '',
            email: initialValues.email ?? '',
            address1: initialValues.address1 ?? '',
            address2: initialValues.address2 ?? '',
            city: initialValues.city ?? '',
            country_code: initialValues.country_code ?? '',
            company: initialValues.company ?? '',
            phone: initialValues.phone ?? '',
            province_code: initialValues.province_code ?? '',
            zip: initialValues.zip ?? '',
            tax_id: initialValues.tax_id ?? '',
        },
    })

    // Google Places Autocomplete
    const { ref } = usePlacesWidget({
        apiKey: 'AIzaSyCZ1NC_ztL-_KkeMQcJACsDV712Zxf09dE',
        onPlaceSelected: (place) => {
            // Extract address components from Google Places API response
            let address1 = ''
            let city = ''
            let zip = ''
            let country_code = ''

            if (place.address_components) {
                // Extract street number and route for address1
                const streetNumber =
                    place.address_components.find((component) =>
                        component.types.includes('street_number')
                    )?.long_name || ''

                const route =
                    place.address_components.find((component) =>
                        component.types.includes('route')
                    )?.long_name || ''

                address1 = streetNumber ? `${streetNumber} ${route}` : route

                // Extract city
                city =
                    place.address_components.find((component) =>
                        component.types.includes('locality')
                    )?.long_name || ''

                // Extract postal code
                zip =
                    place.address_components.find((component) =>
                        component.types.includes('postal_code')
                    )?.long_name || ''

                // Extract country code
                country_code =
                    place.address_components.find((component) =>
                        component.types.includes('country')
                    )?.short_name || ''
            }

            // Update form values using setValues to update multiple fields at once
            form.setValues({
                address1,
                city,
                zip,
                country_code,
            })
        },
        options: {
            types: ['address'],
        },
    })

    const handleSubmit = form.onSubmit((values) => {
        onSubmit(values)
    })

    const noAutofill = {
        autoComplete: 'off',
        'data-1p-ignore': true,
        'data-lpignore': 'true',
        'data-form-type': 'other',
        'data-protonpass-ignore': true,
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-4">
                <TextInput
                    label="Nom du destinataire"
                    placeholder="Nom du destinataire"
                    {...form.getInputProps('name')}
                    {...noAutofill}
                />

                <TextInput
                    label="Entreprise"
                    placeholder="Entreprise"
                    {...form.getInputProps('company')}
                    {...noAutofill}
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <TextInput
                    label="Email"
                    placeholder="Email"
                    type="email"
                    {...form.getInputProps('email')}
                    {...noAutofill}
                />

                <TextInput
                    label="Numéro de téléphone"
                    placeholder="Numéro de téléphone"
                    type="tel"
                    {...form.getInputProps('phone')}
                    {...noAutofill}
                />
            </div>

            <TextInput
                label="Adresse"
                placeholder="Commencez à taper pour rechercher une adresse"
                ref={ref}
                description="Utilisez la recherche pour compléter automatiquement l'adresse"
                {...form.getInputProps('address1')}
                {...noAutofill}
            />

            <TextInput
                label="Adresse 2"
                placeholder="Adresse 2"
                {...form.getInputProps('address2')}
                {...noAutofill}
            />

            <div className="grid grid-cols-2 gap-4">
                <TextInput
                    label="Code postal"
                    placeholder="Code postal"
                    {...form.getInputProps('zip')}
                    {...noAutofill}
                />

                <TextInput
                    label="Ville"
                    placeholder="Ville"
                    {...form.getInputProps('city')}
                    {...noAutofill}
                />
            </div>

            <CountrySelect
                label="Pays"
                placeholder="Pays"
                searchable
                {...form.getInputProps('country_code')}
                {...noAutofill}
            />

            <Button onClick={handleSubmit} fullWidth loading={loading}>
                Enregistrer l'adresse
            </Button>
        </div>
    )
}
